/**
 * 流向树聚合。把后端返回的扁平行聚成表格要的四层结构：
 *
 *  - 发干线：每个目的地一级行，不再往下拆
 *  - 发支线：HUB（如 LAX）→ WHS（如 LA-COI）→ 批次号 → 该批次下的 WHS+线路首字母（如 LA-COI-A）四层
 *
 * 批次还没上线，真实数据先放在每个 WHS 下的「未分批次」里；示例批次由调用方通过 batchProvider 注入，
 * 数字全是 0，只用来把四层结构的样子搭出来。
 */
import type { BagRecord, BagResult, ParcelRow, StageKey } from '../types';
import { ALL_STAGES, MAIN_STAGES, STAGE_BY_BAG, isBagStage, type BatchStatus } from '../constants';
import { NO_WAREHOUSE, UNKNOWN_FLOW, branchPrefix, flowKey } from './flow';

export interface StageCell {
    parcels: number;
    over24: number;
    bags: BagRecord[];
    /** ⓿ 环节的客户大包数 */
    bigBags: number;
    oldestMs: number;
    /** ① 里的「错分未重装」包裹数 */
    err: number;
    errOldestMs: number;
    /** ② 包牌已发、里面还有包裹没发走的包牌数 */
    stragBags: number;
    /** 这些没发走的包裹数 */
    stragParcels: number;
}

export function emptyCells(): Record<StageKey, StageCell> {
    const mk = (): StageCell => ({
        parcels: 0,
        over24: 0,
        bags: [],
        bigBags: 0,
        oldestMs: Infinity,
        err: 0,
        errOldestMs: Infinity,
        stragBags: 0,
        stragParcels: 0
    });
    return { 0: mk(), 1: mk(), 2: mk(), 3: mk(), 4: mk(), 6: mk() };
}

/** 明细接口用的筛选条件 */
export interface FlowFilter {
    g1?: string;
    g2?: string;
    /** true = 发支线，false = 发干线（同名的干线一段码和支线二段不能混） */
    br?: boolean;
}

export type NodeKind = 'trunk' | 'root' | 'wh' | 'real' | 'batch' | 'letter';

export interface FlowNode {
    /** 展开状态用的唯一键 */
    id: string;
    /** 表格里显示的名字 */
    key: string;
    kind: NodeKind;
    filter: FlowFilter;
    cells: Record<StageKey, StageCell>;
    children: FlowNode[];
    /** 示例批次及其下的行（没有真实数据） */
    mock?: boolean;
    batchStatus?: BatchStatus;
}

export function addInto(target: Record<StageKey, StageCell>, src: Record<StageKey, StageCell>): void {
    for (const s of ALL_STAGES) {
        target[s].parcels += src[s].parcels;
        target[s].bigBags += src[s].bigBags;
        target[s].over24 += src[s].over24;
        target[s].bags.push(...src[s].bags);
        target[s].oldestMs = Math.min(target[s].oldestMs, src[s].oldestMs);
        target[s].err += src[s].err;
        target[s].errOldestMs = Math.min(target[s].errOldestMs, src[s].errOldestMs);
        target[s].stragBags += src[s].stragBags;
        target[s].stragParcels += src[s].stragParcels;
    }
}

/** 合计只算 ⓿~④；异常包裹单列、不计入 */
export function totalParcels(c: Record<StageKey, StageCell>): number {
    return MAIN_STAGES.reduce<number>((a, s) => a + c[s].parcels, 0);
}

/** 在途包裹 = ① + ② + ③ + ④；⓿ 大包未入库还没到小包环节，不计入 */
export function inTransitParcels(c: Record<StageKey, StageCell>): number {
    return c[1].parcels + c[2].parcels + c[3].parcels + c[4].parcels;
}

export function totalBags(c: Record<StageKey, StageCell>): number {
    return c[2].bags.length + c[3].bags.length + c[4].bags.length;
}

/** 最长停留（小时）：该格里最早进入当前环节的包裹/包牌到现在的时间 */
export function cellDwellHours(c: StageCell, now: number): number {
    return c.oldestMs === Infinity ? 0 : Math.max(0, (now - c.oldestMs) / 3_600_000);
}

export function maxDwellHours(c: Record<StageKey, StageCell>, now: number): number {
    let ms = Infinity;
    for (const s of MAIN_STAGES) ms = Math.min(ms, c[s].oldestMs);
    return ms === Infinity ? 0 : Math.max(0, (now - ms) / 3_600_000);
}

export interface BatchStub {
    no: string;
    status: BatchStatus;
}

/** 本仓（发支线合计）行的 id，排序时固定置顶 */
export const BRANCH_ID = 'B';

/**
 * 树里由前端生成的行名。只影响显示（FlowNode.key），id 与明细筛选（filter）始终用契约原值，
 * 所以切语言不会让展开状态丢失、也不会让明细查询对不上。
 */
export interface TreeLabels {
    unbatched: string;
    noLetter: string;
    unknownFlow: string;
    noWarehouse: string;
}

const DEFAULT_LABELS: TreeLabels = {
    unbatched: '未分批次',
    noLetter: '未分字母',
    unknownFlow: UNKNOWN_FLOW,
    noWarehouse: NO_WAREHOUSE
};

export function buildFlowTree(
    hub: string,
    parcels: ParcelRow[],
    bags: BagRecord[],
    strag: BagRecord[],
    now: number,
    batchProvider?: (prefix: string, wh: string, now: number) => BatchStub[],
    labels: TreeLabels = DEFAULT_LABELS
): FlowNode[] {
    const trunks = new Map<string, FlowNode>();
    const mk = (id: string, key: string, kind: NodeKind, filter: FlowFilter): FlowNode => ({
        id,
        key,
        kind,
        filter,
        cells: emptyCells(),
        children: []
    });
    const pre = branchPrefix(hub);
    // 第一层：本仓（发支线总数），如 LAX
    const branch = mk(BRANCH_ID, hub, 'root', { br: true });
    const whs = new Map<string, FlowNode>();
    const reals = new Map<string, FlowNode>();
    const leaves = new Map<string, FlowNode>();

    // 沿路径返回该流向命中的所有行的格子（发干线 1 个；发支线 HUB / WHS / 未分批次 / 字母 4 个）
    const groupLabel = (g: string): string =>
        g === UNKNOWN_FLOW ? labels.unknownFlow : g === NO_WAREHOUSE ? labels.noWarehouse : g;
    const cellsFor = (k: { group: string; child: string | null }, stage: StageKey): StageCell[] => {
        if (k.child === null) {
            if (!trunks.has(k.group)) {
                trunks.set(k.group, mk(`T|${k.group}`, groupLabel(k.group), 'trunk', { g1: k.group, br: false }));
            }
            return [trunks.get(k.group)!.cells[stage]];
        }
        if (!whs.has(k.group)) {
            const wh = mk(`${BRANCH_ID}|${k.group}`, `${pre}-${groupLabel(k.group)}`, 'wh', { g1: k.group, br: true });
            const real = mk(`${BRANCH_ID}|${k.group}|real`, labels.unbatched, 'real', { g1: k.group, br: true });
            wh.children.push(real);
            whs.set(k.group, wh);
            reals.set(k.group, real);
            branch.children.push(wh);
        }
        const wh = whs.get(k.group)!;
        const real = reals.get(k.group)!;
        const lk = `${k.group}|${k.child}`;
        if (!leaves.has(lk)) {
            const leaf = mk(
                `${BRANCH_ID}|${lk}`,
                `${pre}-${k.child || `${groupLabel(k.group)}-${labels.noLetter}`}`,
                'letter',
                { g1: k.group, g2: k.child, br: true }
            );
            leaves.set(lk, leaf);
            real.children.push(leaf);
        }
        return [branch.cells[stage], wh.cells[stage], real.cells[stage], leaves.get(lk)!.cells[stage]];
    };

    for (const p of parcels) {
        const stage: StageKey = p.stage === 5 ? 1 : (p.stage as StageKey);
        for (const c of cellsFor({ group: p.g1, child: p.g2 }, stage)) {
            if (p.stage === 5) {
                c.err += p.parcels;
                c.errOldestMs = Math.min(c.errOldestMs, new Date(p.oldest).getTime());
                continue;
            }
            c.parcels += p.parcels;
            c.bigBags += p.bags ?? 0;
            c.over24 += p.over24;
            c.oldestMs = Math.min(c.oldestMs, new Date(p.oldest).getTime());
        }
    }

    for (const b of bags) {
        const stage = STAGE_BY_BAG[b.bagResult as Exclude<BagResult, '已发车'>];
        if (stage === undefined) continue;
        for (const c of cellsFor(flowKey(hub, b.sortingRule || UNKNOWN_FLOW), stage)) {
            c.bags.push(b);
            c.oldestMs = Math.min(c.oldestMs, new Date(b.stageEnteredAt).getTime());
        }
    }

    for (const b of strag) {
        for (const c of cellsFor(flowKey(hub, b.sortingRule || UNKNOWN_FLOW), 2)) {
            c.stragBags += 1;
            c.stragParcels += b.parcelCount;
        }
    }

    // 示例批次：每个 WHS 下挂几个，数字全是 0；批次下的字母行沿用该 WHS 的字母
    if (batchProvider) {
        for (const [g, wh] of whs) {
            const real = reals.get(g)!;
            for (const b of batchProvider(pre, g, now)) {
                const bn = mk(`${BRANCH_ID}|${g}|${b.no}`, b.no, 'batch', { g1: g, br: true });
                bn.mock = true;
                bn.batchStatus = b.status;
                bn.children = real.children.map((lf) => ({
                    ...mk(`${bn.id}|${lf.id}`, lf.key, 'letter', lf.filter),
                    mock: true
                }));
                wh.children.push(bn);
            }
        }
    }

    return [branch, ...trunks.values()].filter((n) => n.kind !== 'root' || n.children.length > 0);
}

/**
 * 卡片告警的统计对象：「流向」= 发干线的目的地行 + 发支线的 WHS 行（本仓合计行展开一层）。
 * 与原型 page.tsx 的 flowNodes 一致，不下钻到批次 / 字母行。
 */
export function flowNodes(tree: FlowNode[]): FlowNode[] {
    return tree.flatMap((n) => (n.kind === 'root' ? n.children : [n]));
}

/** 卡片上的告警聚合：按「有多少个流向触发」来计数 */
export interface StageAlert {
    /** 最长等待超阈值的流向数 */
    late: number;
    /** 所有流向里最久的停留小时 */
    lateMax: number;
    /** 错分未重装件数 / 涉及流向数 */
    err: number;
    errFlows: number;
    /** 包牌已发包裹未发件数 / 涉及流向数 */
    strag: number;
    stragFlows: number;
    /** 包牌堆积的流向数 / 合计包牌数（只看 ②~④） */
    pile: number;
    pileBags: number;
}

export function stageAlerts(
    nodes: FlowNode[],
    now: number,
    overdueThresholdHours: number,
    pileThreshold: number
): Record<StageKey, StageAlert> {
    const out = {} as Record<StageKey, StageAlert>;
    for (const s of ALL_STAGES) {
        const a: StageAlert = { late: 0, lateMax: 0, err: 0, errFlows: 0, strag: 0, stragFlows: 0, pile: 0, pileBags: 0 };
        for (const n of nodes) {
            const c = n.cells[s];
            const h = cellDwellHours(c, now);
            a.lateMax = Math.max(a.lateMax, h);
            if (h > overdueThresholdHours) a.late += 1;
            if (isBagStage(s) && c.bags.length >= pileThreshold) {
                a.pile += 1;
                a.pileBags += c.bags.length;
            }
            if (c.err > 0) {
                a.err += c.err;
                a.errFlows += 1;
            }
            if (c.stragParcels > 0) {
                a.strag += c.stragParcels;
                a.stragFlows += 1;
            }
        }
        out[s] = a;
    }
    return out;
}

/** 客户筛选（包牌）：包牌里可能混装多个客户，筛选后包牌只算该客户的包裹数，没有该客户的包牌去掉 */
export function filterBagsByCustomer(bags: BagRecord[], customer: string): BagRecord[] {
    if (!customer) return bags;
    return bags.flatMap((b) => {
        const n = b.customers?.[customer] ?? 0;
        return n > 0 ? [{ ...b, parcelCount: n }] : [];
    });
}

/** 客户筛选（包裹聚合行） */
export function filterParcelsByCustomer(parcels: ParcelRow[], customer: string): ParcelRow[] {
    return customer ? parcels.filter((p) => p.customer === customer) : parcels;
}
